// ActivityForm — one unified form everyone fills.
// Left column: Attendance (2-col grid) + Notes. Right column: Activity counts
// grouped by category. Bottom: totals strip color-coded per category.

import React from 'react';
import { Input, InputNumber, Tooltip } from 'antd';
import { tokens, categoryAccent, subtotalAccent } from './theme';
import { computeSubtotals, computeDayGrandTotal, parseCounts } from './manualReportUtils';

const ATTENDANCE_FIELDS = [
  ['loginTime',   'Login Time',     '9:30 AM'],
  ['logoutTime',  'Logout Time',    '6:30 PM'],
  ['breakIn',     'Break In',       '1:00 PM'],
  ['breakOut',    'Break Out',      '2:00 PM'],
  ['duration',    'Duration',       '9 Hours'],
  ['permissions', 'Permissions',    'Leave / WFH'],
];

const CardLabel = ({ children, accent }) => (
  <div style={{
    ...tokens.type.label,
    color: accent || tokens.color.textFaint,
    marginBottom: tokens.space.sm,
  }}>{children}</div>
);

const Card = ({ children, padding, accent }) => (
  <div style={{
    background: tokens.color.surface,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    padding: padding ?? tokens.space.lg,
    boxSizing: 'border-box',
    width: '100%',
    display: 'block',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {accent && (
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, background: accent, opacity: 0.8,
      }} />
    )}
    {children}
  </div>
);

const AttField = ({ label, value, onChange, placeholder, disabled }) => (
  <div>
    <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginBottom: 6 }}>{label}</div>
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%', borderRadius: tokens.radius.md }}
    />
  </div>
);

const CountRow = ({ label, value, onChange, disabled, accent }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space.sm,
    padding: '7px 10px',
    borderRadius: tokens.radius.sm,
    background: tokens.color.surface,
    border: `1px solid ${tokens.color.divider}`,
    minWidth: 0,
  }}>
    <Tooltip title={label} mouseEnterDelay={0.4}>
      <div style={{
        flex: 1, minWidth: 0,
        fontSize: 12.5, color: tokens.color.text,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        lineHeight: 1.3,
      }}>{label}</div>
    </Tooltip>
    <InputNumber
      size="small"
      min={0}
      value={Number.isFinite(value) ? value : null}
      onChange={(v) => onChange(v == null || isNaN(v) ? null : Number(v))}
      placeholder="0"
      disabled={disabled}
      controls={false}
      style={{
        width: 64, flexShrink: 0, textAlign: 'right',
        fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        color: accent.color,
      }}
    />
  </div>
);

const TotalTile = ({ label, value, accent, big }) => (
  <div style={{
    flex: '1 1 140px',
    minWidth: 130,
    background: accent.bg,
    border: `1px solid ${accent.border}`,
    borderRadius: tokens.radius.md,
    padding: big ? '14px 18px' : '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }}>
    <div style={{
      ...tokens.type.caption,
      fontSize: 10.5,
      color: accent.color,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{label}</div>
    <div style={{
      fontSize: big ? 26 : 22,
      fontWeight: 800,
      color: accent.color,
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: -0.5,
      lineHeight: 1,
    }}>{value || 0}</div>
  </div>
);

export default function ActivityForm({ config, entry, onChange, readOnly }) {
  if (!config?.categories) return null;

  const attendance = entry?.attendance || {};
  const counts = parseCounts(entry);
  const subtotals = computeSubtotals(config, counts);
  const grandTotal = computeDayGrandTotal(config, counts);

  const setAttendance = (field, val) => onChange({ ...entry, attendance: { ...attendance, [field]: val } });
  const setCount = (key, val) => onChange({ ...entry, counts: { ...counts, [key]: val } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.lg }}>

      {/* Two-column body: Left = Attendance + Notes (stacked), Right = Activity Counts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(320px, 400px) 1fr',
        gap: tokens.space.lg,
        alignItems: 'start',
      }}>

        {/* LEFT — Attendance + Notes stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.lg }}>
          <Card accent={tokens.color.accent}>
            <CardLabel accent={tokens.color.accent}>Attendance</CardLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.space.md }}>
              {ATTENDANCE_FIELDS.map(([k, label, ph]) => (
                <AttField
                  key={k}
                  label={label}
                  value={attendance[k]}
                  onChange={(v) => setAttendance(k, v)}
                  placeholder={ph}
                  disabled={readOnly}
                />
              ))}
            </div>
          </Card>

          <Card accent="#7c3aed">
            <CardLabel accent="#7c3aed">Notes</CardLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.md }}>
              <div>
                <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginBottom: 6 }}>Lead IDs</div>
                <Input.TextArea
                  value={entry?.leadIds || ''}
                  onChange={(e) => onChange({ ...entry, leadIds: e.target.value })}
                  placeholder="30223, 32181, 32182…"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={readOnly}
                  style={{ ...tokens.type.mono, fontSize: 12, borderRadius: tokens.radius.md }}
                />
              </div>
              <div>
                <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginBottom: 6 }}>Remarks</div>
                <Input.TextArea
                  value={entry?.remarks || ''}
                  onChange={(e) => onChange({ ...entry, remarks: e.target.value })}
                  placeholder="Anything noteworthy about today…"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={readOnly}
                  style={{ borderRadius: tokens.radius.md }}
                />
              </div>
              <div>
                <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginBottom: 6 }}>Meetings with Team</div>
                <Input.TextArea
                  value={entry?.meetings || ''}
                  onChange={(e) => onChange({ ...entry, meetings: e.target.value })}
                  placeholder="Who, when, what was discussed…"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={readOnly}
                  style={{ borderRadius: tokens.radius.md }}
                />
              </div>
              <div>
                <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginBottom: 6 }}>Complaints / Issues</div>
                <Input.TextArea
                  value={entry?.complaints || ''}
                  onChange={(e) => onChange({ ...entry, complaints: e.target.value })}
                  placeholder="Any issues raised today…"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={readOnly}
                  style={{ borderRadius: tokens.radius.md }}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT — Activity counts */}
        <Card>
          <CardLabel>Activity Counts</CardLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.md }}>
            {config.categories.map((cat) => {
              const accent = categoryAccent(cat.key);
              return (
                <div key={cat.key} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.space.xs,
                  paddingLeft: tokens.space.sm,
                  borderLeft: `3px solid ${accent.color}`,
                }}>
                  <div style={{
                    ...tokens.type.label,
                    fontSize: 10.5,
                    color: accent.color,
                    fontWeight: 700,
                    marginBottom: 2,
                  }}>{cat.label}</div>
                  <div style={{
                    display: 'grid',
                    // auto-FILL keeps empty grid tracks when a category has
                    // fewer fields, so 2-field rows (Emails/Calls) match the
                    // width of 3-field rows (Leads/LinkedIn) instead of
                    // stretching to fill the whole row.
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 6,
                  }}>
                    {cat.fields.map(f => (
                      <CountRow
                        key={f.key}
                        label={f.label}
                        value={counts[f.key]}
                        onChange={(v) => setCount(f.key, v)}
                        disabled={readOnly}
                        accent={accent}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

      </div>

      {/* Totals strip */}
      <Card padding={tokens.space.md}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space.md, flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', flexDirection: 'column',
            paddingRight: tokens.space.md,
            borderRight: `1px solid ${tokens.color.border}`,
            minWidth: 120,
          }}>
            <div style={{ ...tokens.type.label, color: tokens.color.textFaint }}>Day Totals</div>
            <div style={{ ...tokens.type.caption, color: tokens.color.textMuted, marginTop: 2 }}>Auto-calculated</div>
          </div>
          {(config.subtotals || []).map(s => (
            <TotalTile key={s.key} label={s.label} value={subtotals[s.key] || 0} accent={subtotalAccent(s.key)} />
          ))}
          <TotalTile
            label="Grand Total"
            value={grandTotal}
            accent={{ color: tokens.color.accent, bg: tokens.color.accentBg, border: tokens.color.accentBorder }}
            big
          />
        </div>
      </Card>
    </div>
  );
}
